'use client';

import { useTranslations } from 'next-intl';

export type AspectRatioOrientation = 'portrait' | 'square' | 'landscape';

const ASPECT_RATIO_ORIENTATIONS: readonly AspectRatioOrientation[] = [
  'portrait',
  'square',
  'landscape',
];

const ORIENTATION_PREVIEW_DIMENSIONS: Record<
  AspectRatioOrientation,
  { width: number; height: number }
> = {
  portrait: { width: 13, height: 20 },
  square: { width: 18, height: 18 },
  landscape: { width: 20, height: 13 },
};

const OrientationShapePreview = ({ orientation }: { orientation: AspectRatioOrientation }) => {
  const dimensions = ORIENTATION_PREVIEW_DIMENSIONS[orientation];

  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden="true">
      <span
        className="block rounded-[2px] border-2 border-current bg-white/90 shadow-sm dark:bg-slate-950/90"
        style={dimensions}
      />
    </span>
  );
};

export const AspectRatioOrientationTabs = ({
  selectedOrientation,
  onOrientationChange,
  allowAll = false,
  allLabel,
  allAriaLabel,
  stackOnMobile = false,
  onAllChange,
  ariaLabel,
}: {
  selectedOrientation: AspectRatioOrientation | null;
  onOrientationChange: (orientation: AspectRatioOrientation) => void;
  allowAll?: boolean;
  allLabel?: string;
  allAriaLabel?: string;
  stackOnMobile?: boolean;
  onAllChange?: () => void;
  ariaLabel?: string;
}) => {
  const t = useTranslations('create.sizeAndQualityModal');
  const optionClassName = (selected: boolean) =>
    `inline-flex min-w-0 items-center justify-center gap-1 rounded-full ${stackOnMobile ? 'flex-col px-1 sm:flex-row' : 'px-1.5'} py-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 sm:gap-1.5 sm:px-4 sm:text-sm ${
      selected
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/12 dark:text-amber-200'
        : 'text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-100'
    }`;

  return (
    <div
      role="group"
      aria-label={ariaLabel ?? t('slider.title')}
      className={`grid w-full rounded-full border border-gray-200 bg-white p-1 shadow-sm dark:border-slate-700 dark:bg-slate-900/70 sm:inline-flex sm:w-auto ${
        allowAll ? 'grid-cols-4' : 'grid-cols-3'
      }`}
    >
      {allowAll && (
        <button
          type="button"
          aria-pressed={selectedOrientation === null}
          aria-label={allAriaLabel}
          onClick={onAllChange}
          className={optionClassName(selectedOrientation === null)}
        >
          {stackOnMobile && <span className="h-5 w-5 sm:hidden" aria-hidden="true" />}
          <span className="min-w-0 truncate">{allLabel ?? t('slider.all')}</span>
        </button>
      )}
      {ASPECT_RATIO_ORIENTATIONS.map(orientation => {
        const selected = selectedOrientation === orientation;

        return (
          <button
            key={orientation}
            type="button"
            aria-pressed={selected}
            onClick={() => onOrientationChange(orientation)}
            className={optionClassName(selected)}
          >
            <OrientationShapePreview orientation={orientation} />
            <span className="min-w-0 truncate">{t(`slider.orientation.${orientation}`)}</span>
          </button>
        );
      })}
    </div>
  );
};
